const HIGHLIGHT_CLASS = 'search-highlight';

export default class SearchHelper {
    setup() {
        document.addEventListener('DOMContentLoaded', () => {
            this.matches = [];
            this.currentIndex = -1;
            this.initializeElements();
            this.addEventListeners();

            const urlParams = new URLSearchParams(window.location.search);
            const searchParam = urlParams.get('traff');

            if (searchParam) {
                this.searchInputs.forEach(input => input.value = searchParam);
                this.handleSearch({ target: { value: searchParam } });
                this.handleNext();
            }
        });
    }

    initializeElements() {
        this.searchBoxDevice = document.querySelector('.search-box.device');
        this.searchInputs = document.querySelectorAll('.search-input');
        this.searchButtons = document.querySelectorAll('.search-button');
        this.nextButtons = document.querySelectorAll('.next-button');
        this.prevButtons = document.querySelectorAll('.prev-button');
        this.searchStatus = document.querySelectorAll('.search-status');
        this.content = document.querySelector('.digital-publication');
        this.openSearchButton = document.querySelector('.open-search-button')
        this.closeSearchButton = document.querySelector('.close-search-button');
        this.clearButton = document.querySelector('.clear-button');
    }

    addEventListeners() {
        this.searchInputs.forEach(searchBox => {
            searchBox.addEventListener('keyup', this.handleSearch.bind(this));
            searchBox.addEventListener('keydown', this.handleSearchTrigger.bind(this));
        });
        this.searchButtons.forEach(searchButton => {
            searchButton.addEventListener('click', this.handleSearch.bind(this));
        });
        this.nextButtons.forEach(nextButton => {
            nextButton.addEventListener('click', this.handleNext.bind(this));
        });
        this.prevButtons.forEach(prevButton => {
            prevButton.addEventListener('click', this.handlePrev.bind(this));
        });

        this.openSearchButton.addEventListener('click', this.handleOpenSearch.bind(this));
        this.closeSearchButton.addEventListener('click', this.handleOpenSearch.bind(this));
        this.clearButton.addEventListener('click', this.handleClear.bind(this));
    }

    handleSearchTrigger(event) {
        if (event.key === 'Enter') {
            this.handleNext();
        }
    }

    handleOpenSearch() {
        this.handleClear();
        this.searchBoxDevice.classList.toggle('active');
        this.searchBoxDevice.closest('.head').classList.toggle('sticky-search');
    }

    handleSearch(event) {
        const searchText = event.target.value;

        if (!searchText) {
            this.handleClear();
            return;
        }

        this.searchInputs.forEach(searchBox => {
            searchBox.classList.add('active');
        });

        this.clearButton.classList.add('active');

        if (searchText.length < 3) {
            return;
        }

        this.highlightSearch(searchText);
        if (this.matches.length > 0) {
            this.nextButtons.forEach(button => button.disabled = false);
            this.prevButtons.forEach(button => button.disabled = false);
        } else {
            this.nextButtons.forEach(button => button.disabled = true);
            this.prevButtons.forEach(button => button.disabled = true);
        }

        this.updateSearchStatus();

        this.searchInputs.forEach(input => {
            input.value = searchText;
        });
    }

    handleNext() {
        if (this.matches.length > 0) {
            const nextIndex = (this.currentIndex + 1) % this.matches.length;
            this.scrollToMatch(nextIndex);
        }
    }

    handlePrev() {
        if (this.matches.length > 0) {
            const prevIndex = (this.currentIndex - 1 + this.matches.length) % this.matches.length;
            this.scrollToMatch(prevIndex);
        }
    }

    handleClear() {
        this.searchInputs.forEach(searchBox => searchBox.value = '');
        this.clearHighlights();
        this.matches = [];
        this.currentIndex = -1;
        this.nextButtons.forEach(button => button.disabled = true);
        this.prevButtons.forEach(button => button.disabled = true);
        this.updateSearchStatus();
        this.clearButton.classList.remove('active');
        this.searchInputs.forEach(searchBox => {
            searchBox.classList.remove('active');
        });
    }

    highlightSearch(text) {
        const regex = new RegExp(text.split('').join('\u00AD?'), 'gi');
        this.clearHighlights();
        const textNodes = this.getTextNodesMatchingRegex(regex);

        textNodes.forEach((node) => {
            const fragment = this.createHighlightedFragment(node, regex);
            node.parentNode.replaceChild(fragment, node);
        });
    }

    getTextNodesMatchingRegex(regex) {
        let node;
        const textNodes = [];
        const walker = document.createTreeWalker(
            this.content, NodeFilter.SHOW_TEXT, null
        );

        while ((node = walker.nextNode())) {
            if (
                node.nodeValue.match(regex) &&
                !node.parentElement.closest('.digital-publication__toc') &&
                !node.parentElement.closest('.read-more')
            ) {
                textNodes.push(node);
            }
        }

        return textNodes;
    }

    createHighlightedFragment(node, regex) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(node.nodeValue)) !== null) {
            const part = node.nodeValue.substring(lastIndex, match.index);
            fragment.appendChild(document.createTextNode(part));

            const span = document.createElement('span');
            span.className = HIGHLIGHT_CLASS;
            span.textContent = match[0];
            fragment.appendChild(span);
            this.matches.push(span);

            lastIndex = regex.lastIndex;
        }

        fragment.appendChild(
            document.createTextNode(node.nodeValue.substring(lastIndex))
        );
        regex.lastIndex = 0;

        return fragment;
    }

    clearHighlights() {
        const highlightedElements = document.querySelectorAll('.' + HIGHLIGHT_CLASS);
        highlightedElements.forEach(element => {
            const parent = element.parentNode;
            parent.replaceChild(
                document.createTextNode(element.textContent), element
            );
            parent.normalize();
        });
        this.matches = [];
    }

    scrollToMatch(index) {
        if (index >= 0 && index < this.matches.length) {
            const target = this.matches[index];

            if (!target) {
                return;
            }

            document.querySelectorAll('.' + HIGHLIGHT_CLASS).forEach(element => {
                element.removeAttribute('aria-current');
            });

            const prefersReducedMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches;

            target.setAttribute(
                'aria-current', 'true'
            );
            target.scrollIntoView({
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
                block: 'center'
            });
            this.currentIndex = index;
            this.updateSearchStatus();

            target.focus();
        }
    }

    updateSearchStatus() {
        const statusText = this.matches.length > 0
            ? `${this.currentIndex + 1}/${this.matches.length}`
            : '0/0';

        this.searchStatus.forEach(status => {
            status.textContent = statusText;
        });
    }
}